import { config } from '../../config/config';
import { getHasuraClaimsFromToken, getStoredIdToken } from '../../authToken';

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');
const GRAPHQL_URL = trimTrailingSlash(process.env.REACT_APP_BACKEND_URL || config.backend_url || '');
const ADMIN_PORTAL_ROLES = ['admin', 'affiliater_admin', 'super_user', 'logitix_user', 'marketing_user'];

const resolveHasuraRoleHeader = (token: string): string => {
  const claims = getHasuraClaimsFromToken(token);
  if (!claims) return '';

  const allowedRoles = Array.isArray(claims?.['x-hasura-allowed-roles'])
    ? claims['x-hasura-allowed-roles']
    : [];
  const defaultRole = claims?.['x-hasura-default-role'] || '';
  const requestedRole = localStorage.getItem('x-hasura-user-role') || '';

  if (
    requestedRole &&
    ADMIN_PORTAL_ROLES.includes(requestedRole) &&
    allowedRoles.includes(requestedRole)
  ) {
    return requestedRole;
  }

  if (defaultRole && allowedRoles.includes(defaultRole)) {
    return defaultRole;
  }

  return '';
};

const authHeaders = (): Record<string, string> => {
  const token = getStoredIdToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (token) headers.Authorization = `Bearer ${token}`;

  if (token) {
    const hasuraRole = resolveHasuraRoleHeader(token);
    if (hasuraRole) {
      headers['x-hasura-role'] = hasuraRole;
    }
  }

  return headers;
};

const executeGraphQL = async <T = any>(query: string, variables: Record<string, any> = {}): Promise<T> => {
  if (!GRAPHQL_URL) {
    throw new Error('Missing GraphQL URL configuration');
  }

  const response = await fetch(GRAPHQL_URL, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ query, variables }),
  });

  const payload = await response.json();
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`);
  }
  if (payload?.errors?.[0]?.message) {
    throw new Error(payload.errors[0].message);
  }

  return payload?.data as T;
};

const GET_ANNUAL_GOALS = `
query GetAnnualGoals($year: Int!) {
  annual_goals(where: {year: {_eq: $year}}) {
    id
    year
    revenue_goal
    profit_goal
    actual_revenue
    actual_profit
    created_at
    updated_at
  }
}
`;

const UPSERT_ANNUAL_GOALS = `
mutation UpsertAnnualGoals($year: Int!, $revenue_goal: numeric!, $profit_goal: numeric!) {
  insert_annual_goals_one(
    object: {year: $year, revenue_goal: $revenue_goal, profit_goal: $profit_goal}
    on_conflict: {
      constraint: annual_goals_year_key
      update_columns: [revenue_goal, profit_goal, updated_at]
    }
  ) {
    id
    year
    revenue_goal
    profit_goal
    actual_revenue
    actual_profit
    created_at
    updated_at
  }
}
`;

const GET_MONTHLY_PERFORMANCE = `
query GetMonthlyPerformance($year: Int!) {
  monthly_performance(where: {year: {_eq: $year}}, order_by: {month: asc}) {
    id
    year
    month
    revenue
    profit
    goal_amount
    profit_goal
    leagues
    events
    created_at
    updated_at
  }
}
`;

const UPSERT_MONTHLY_PERFORMANCE = `
mutation UpsertMonthlyPerformance(
  $year: Int!,
  $month: Int!,
  $revenue: numeric!,
  $profit: numeric!,
  $goal_amount: numeric!,
  $profit_goal: numeric!,
  $leagues: [String!]!,
  $events: [String!]!
) {
  insert_monthly_performance_one(
    object: {
      year: $year,
      month: $month,
      revenue: $revenue,
      profit: $profit,
      goal_amount: $goal_amount,
      profit_goal: $profit_goal,
      leagues: $leagues,
      events: $events
    }
    on_conflict: {
      constraint: monthly_performance_year_month_key
      update_columns: [revenue, profit, goal_amount, profit_goal, leagues, events, updated_at]
    }
  ) {
    id
    year
    month
    revenue
    profit
    goal_amount
    profit_goal
    leagues
    events
    created_at
    updated_at
  }
}
`;

const GET_MAJOR_EVENTS = `
query GetMajorEvents {
  major_events(order_by: {event_date: asc}) {
    id
    name
    event_date
    created_at
    updated_at
  }
}
`;

const INSERT_MAJOR_EVENT = `
mutation InsertMajorEvent($name: String!, $event_date: timestamptz) {
  insert_major_events_one(object: {name: $name, event_date: $event_date}) {
    id
    name
    event_date
    created_at
    updated_at
  }
}
`;

const GET_ACTIVE_LEAGUES_FOR_GOALS = `
query GetActiveLeaguesForGoals {
  leagues(
    where: {
      l_deleted_at: {_is_null: true},
      l_is_active: {_eq: true}
    },
    order_by: {l_name: asc}
  ) {
    l_id
    l_name
  }
}
`;

const GET_MARKETPLACES_FOR_GOALS = `
query GetMarkeplaces {
  marketplaces(order_by: {name: asc}) {
    id
    name
    vl_fee_percentage
    active
  }
}
`;

const GET_SOLD_TICKET_REVENUE_BY_YEAR = `
query GetSoldTicketRevenueByYear($startDate: timestamptz!, $endDate: timestamptz!) {
  ticket_placement(
    where: {
      tp_status: {_eq: Sold}
      tp_is_support_vanderbilt_nil_fund: {_eq: false}
      tp_sold_at: {_gte: $startDate, _lt: $endDate}
    }
  ) {
    tp_sold_at
    tp_logitix_amount
    tp_quick_cut_amount
    tp_execution_type
    user {
      u_pricing_mode
    }
  }
}
`;

// ── Annual Goals ────────────────────────────────────────────────────────────
export const fetchAnnualGoals = async (year: number) => {
  try {
    const data = await executeGraphQL<{ annual_goals?: any[] }>(GET_ANNUAL_GOALS, { year });
    const goal = data?.annual_goals?.[0] || null;
    if (!goal) return null;
    return {
      ...goal,
      actual_revenue: Number(goal.actual_revenue || 0),
      actual_profit: Number(goal.actual_profit || 0),
    };
  } catch (error: any) {
    const message = String(error?.message || "").toLowerCase();
    if (message.includes("field 'annual_goals' not found")) {
      return null;
    }
    throw error;
  }
};

export const upsertAnnualGoals = async (year: number, revenue_goal: number, profit_goal: number) => {
  const data = await executeGraphQL<{ insert_annual_goals_one?: any }>(UPSERT_ANNUAL_GOALS, {
    year,
    revenue_goal,
    profit_goal,
  });
  return data?.insert_annual_goals_one || null;
};

// ── Monthly Performance ──────────────────────────────────────────────────────
export const fetchMonthlyPerformance = async (year: number) => {
  try {
    const data = await executeGraphQL<{ monthly_performance?: any[] }>(GET_MONTHLY_PERFORMANCE, { year });
    return data?.monthly_performance || [];
  } catch (error: any) {
    const message = String(error?.message || "").toLowerCase();
    if (message.includes("field 'monthly_performance' not found")) {
      return [];
    }
    throw error;
  }
};

export const upsertMonthlyPerformance = async (
  year: number,
  month: number,
  revenue: number,
  profit: number,
  goal_amount: number,
  profit_goal: number,
  leagues: string[],
  events: string[],
) => {
  const data = await executeGraphQL<{ insert_monthly_performance_one?: any }>(UPSERT_MONTHLY_PERFORMANCE, {
    year,
    month,
    revenue,
    profit,
    goal_amount,
    profit_goal,
    leagues,
    events,
  });
  return data?.insert_monthly_performance_one || null;
};

// ── Major Events ──────────────────────────────────────────────────────────────
export const fetchMajorEvents = async () => {
  try {
    const data = await executeGraphQL<{ major_events?: any[] }>(GET_MAJOR_EVENTS);
    return data?.major_events || [];
  } catch (error: any) {
    const message = String(error?.message || "").toLowerCase();
    // Older Hasura schemas may not expose major_events table.
    // Goals page should still render using available datasets.
    if (message.includes("field 'major_events' not found")) {
      return [];
    }
    throw error;
  }
};

export const addMajorEvent = async (name: string, event_date?: string) => {
  const data = await executeGraphQL<{ insert_major_events_one?: any }>(INSERT_MAJOR_EVENT, {
    name,
    event_date: event_date || null,
  });
  return data?.insert_major_events_one || null;
};

// ── Active Leagues ─────────────────────────────────────────────────────────────
export const fetchActiveLeagues = async () => {
  const data = await executeGraphQL<{ leagues?: Array<{ l_name?: string }> }>(GET_ACTIVE_LEAGUES_FOR_GOALS);
  return (data?.leagues || []).map((league) => league.l_name).filter(Boolean);
};

// ── Marketplaces (Dynamic Fee Configuration) ──────────────────────────────────
export const fetchMarketplaces = async () => {
  const data = await executeGraphQL<{
    marketplaces?: Array<{ 
      id: string;
      name: string; 
      vl_fee_percentage: number;
      active: boolean;
    }>;
  }>(GET_MARKETPLACES_FOR_GOALS);
  
  return data?.marketplaces || [];
};

// ── Ticket Stats (actual revenue/profit/tickets from sold ticket data) ─────────
// Formula: Profit = (Revenue × User Fee %) − (Marketplace Fee %)
// Dynamic Marketplace Fees: Fetched from database based on actual tickets sold
// Example: $100 sold on StubHub with 15% user fee & 6% marketplace fee = $9 profit
export const fetchTicketStats = async (year: number) => {
  // Fetch ticket data and marketplace fees in parallel
  const [ticketData, marketplaces] = await Promise.all([
    executeGraphQL<{
      ticket_placement?: Array<{ 
        tp_sold_at?: string; 
        tp_logitix_amount?: number | string; 
        tp_quick_cut_amount?: number | string;
        user?: { u_pricing_mode?: string };
        tp_execution_type?: string;
      }>;
    }>(GET_SOLD_TICKET_REVENUE_BY_YEAR, {
      startDate: `${year}-01-01T00:00:00Z`,
      endDate: `${year + 1}-01-01T00:00:00Z`,
    }),
    fetchMarketplaces(),
  ]);

  // Build dynamic marketplace fee map from database
  const marketplaceFeeMap: { [key: string]: number } = {};
  for (const marketplace of marketplaces) {
    marketplaceFeeMap[marketplace.name] = marketplace.vl_fee_percentage;
  }

  // User fee configuration (default values)
  const userFeeConfig: { [key: string]: number } = {
    'self-pricing': 15,
    'self_pricing': 15,
    'concierge': 10,
    'default': 12,
  };

  const getUserFee = (pricingMode?: string): number => 
    userFeeConfig[pricingMode || 'default'] || userFeeConfig['default'];

  const getMarketplaceFee = (executionType?: string): number => {
    // First try to find by marketplace name/execution type
    if (executionType && marketplaceFeeMap[executionType]) {
      return marketplaceFeeMap[executionType];
    }
    // Fallback to first active marketplace average or default
    const activeMarketplaces = marketplaces.filter(m => m.active);
    if (activeMarketplaces.length > 0) {
      const avgFee = activeMarketplaces.reduce((sum, m) => sum + m.vl_fee_percentage, 0) / activeMarketplaces.length;
      return avgFee;
    }
    return 5; // Global default
  };

  const monthlyStats = Array.from({ length: 12 }, (_, index) => ({
    month: index + 1,
    revenue: 0,
    profit: 0,
    tickets: 0,
  }));

  // Process each ticket with dynamic marketplace fees
  for (const ticket of ticketData?.ticket_placement || []) {
    if (!ticket?.tp_sold_at) continue;
    const monthIndex = new Date(ticket.tp_sold_at).getMonth();
    if (monthIndex < 0 || monthIndex > 11) continue;

    const revenue = Number(ticket.tp_logitix_amount) || 0;
    
    // Apply correct profit formula with dynamic marketplace fees
    const userFeePct = getUserFee(ticket.user?.u_pricing_mode);
    const marketplaceFeePct = getMarketplaceFee(ticket.tp_execution_type);
    const userFeeAmount = revenue * (userFeePct / 100);
    const marketplaceFeeAmount = revenue * (marketplaceFeePct / 100);
    const profit = userFeeAmount - marketplaceFeeAmount;

    monthlyStats[monthIndex].revenue += revenue;
    monthlyStats[monthIndex].profit += profit;
    monthlyStats[monthIndex].tickets += 1;
  }

  const annual = monthlyStats.reduce(
    (acc, month) => ({
      revenue: acc.revenue + month.revenue,
      profit: acc.profit + month.profit,
      tickets: acc.tickets + month.tickets,
    }),
    { revenue: 0, profit: 0, tickets: 0 }
  );

  console.log('📊 [Goals API] Dynamic Marketplace Fees Loaded:', marketplaceFeeMap);
  console.log('💰 [Goals API] Annual Stats:', annual);

  return { monthly: monthlyStats, annual };
};
